import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../services/authContext';
import { apiService } from '../services/apiService';
import useSetupStatus from '../hooks/useSetupStatus';

const ROLE_LABELS = {
  admin: 'Administrator',
  teacher: 'Teacher',
  student: 'Student',
  parent: 'Parent/Guardian',
};

const ROLE_COLORS = {
  admin: '#D32F2F',
  teacher: '#1976D2',
  student: '#388E3C',
  parent: '#F57C00',
};

const Badge = ({ text, color }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '0.2rem 0.6rem',
      borderRadius: 12,
      fontSize: '0.78rem',
      fontWeight: 600,
      background: `${color}22`,
      color,
    }}
  >
    {text}
  </span>
);

const StatCard = ({ icon, label, value, to, color = 'var(--primary-color)' }) => (
  <div className="stat-card">
    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{icon}</div>
    <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-light)' }}>{label}</h3>
    <div className="stat-value" style={{ color }}>{value}</div>
    {to && <Link to={to} className="stat-link">View all</Link>}
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="dashboard-section">
    <div className="card-header">
      <h2>{title}</h2>
    </div>
    {children}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="no-data-message">{message}</div>
);

const SETUP_STEPS = [
  { key: 'hasProgrammes',       icon: '🏛️', label: 'Programmes',        description: 'Create at least one academic programme.',          to: '/programmes',  action: 'Create Programme' },
  { key: 'hasClasses',          icon: '🏫', label: 'Classes / Batches',  description: 'Create classes under your programmes.',             to: '/programmes',  action: 'Create Class' },
  { key: 'hasTeachers',         icon: '👨🏫', label: 'Lecturers',        description: 'Add at least one lecturer to the system.',          to: '/lecturers',   action: 'Add Lecturer' },
  { key: 'hasModules',          icon: '📚', label: 'Modules',            description: 'Create modules and assign them to classes.',        to: '/modules',     action: 'Create Module' },
  { key: 'hasAssignedTeachers', icon: '🔗', label: 'Lecturer Assignments', description: 'Assign lecturers to classes or modules.',          to: '/programmes',  action: 'Assign Lecturers' },
  { key: 'hasStudents',         icon: '🎓', label: 'Students',           description: 'Enrol at least one student.',                       to: '/students',    action: 'Enrol Student' },
];

const SetupChecklist = ({ status }) => {
  const incomplete = SETUP_STEPS.filter(s => !status[s.key]);
  if (incomplete.length === 0) return null;

  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid #f0c040',
      borderLeft: '5px solid #f0c040',
      borderRadius: 10,
      padding: '1.25rem 1.5rem',
      marginBottom: '1.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '1.3rem' }}>🚀</span>
        <strong style={{ fontSize: '1rem' }}>System Setup — {SETUP_STEPS.length - incomplete.length}/{SETUP_STEPS.length} steps complete</strong>
      </div>
      <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: 'var(--text-light)' }}>
        Complete these steps to unlock all features:
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {SETUP_STEPS.map(step => {
          const done = status[step.key];
          return (
            <div key={step.key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: '0.5rem',
              padding: '0.6rem 0.85rem',
              borderRadius: 7,
              background: done ? '#f0faf0' : 'var(--bg-secondary, #f9f9f9)',
              border: `1px solid ${done ? '#a5d6a7' : 'var(--border-color)'}`,
              opacity: done ? 0.7 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.1rem' }}>{done ? '✅' : step.icon}</span>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem',
                    textDecoration: done ? 'line-through' : 'none',
                    color: done ? 'var(--text-light)' : 'inherit' }}>
                    {step.label}
                  </span>
                  {!done && <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{step.description}</div>}
                </div>
              </div>
              {!done && (
                <Link to={step.to} className="btn btn-primary"
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.85rem' }}>
                  {step.action}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminDashboard = ({ data, setupStatus }) => {
  const stats = data.stats || {};
  const recentUsers = data.recentUsers || [];

  return (
    <>
      {setupStatus && <SetupChecklist status={setupStatus} />}
      <div className="stats-grid">
        <StatCard icon="🎓" label="Learners" value={stats.students || 0} to="/students" />
        <StatCard icon="👨‍🏫" label="Staff" value={stats.teachers || 0} to="/lecturers" />
        <StatCard icon="🏫" label="Classes" value={stats.classes || 0} to="/programmes" />
        <StatCard icon="📚" label="Modules" value={stats.modules || stats.subjects || 0} to="/modules" />
        <StatCard icon="✉️" label="Unread Messages" value={data.unreadMessages || 0} to="/messages" />
      </div>

      <SectionCard title="Recent Users">
        {recentUsers.length === 0 ? (
          <EmptyState message="No users found yet." />
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.role}</td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </>
  );
};

const TeacherDashboard = ({ data }) => {
  const stats = data.stats || {};
  const classes = data.classes || [];
  const upcomingAssessments = data.upcomingAssessments || [];

  return (
    <>
      <div className="stats-grid">
        <StatCard icon="🏫" label="My Classes" value={stats.classes || 0} to="/programmes" />
        <StatCard icon="🎓" label="My Learners" value={stats.students || 0} to="/students" />
        <StatCard icon="📝" label="Upcoming Assessments" value={upcomingAssessments.length} to="/exams" />
        <StatCard icon="✉️" label="Unread Messages" value={data.unreadMessages || 0} to="/messages" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        <SectionCard title="Assigned Classes">
          {classes.length === 0 ? (
            <EmptyState message="No classes assigned yet." />
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Learners</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map(item => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.studentCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Upcoming Assessments">
          {upcomingAssessments.length === 0 ? (
            <EmptyState message="No upcoming assessments." />
          ) : (
            upcomingAssessments.map(item => (
              <div key={item.id} className="recent-item">
                <div className="recent-item-title">{item.title}</div>
                <div className="recent-item-subtitle">{item.subject} · {item.class}</div>
                <div className="recent-item-date">{new Date(item.date).toLocaleDateString()}</div>
              </div>
            ))
          )}
        </SectionCard>
      </div>
    </>
  );
};

const StudentDashboard = ({ data }) => {
  const attendance = data.attendance || {};
  const upcomingAssessments = data.upcomingAssessments || [];

  return (
    <>
      <div className="stats-grid">
        <StatCard
          icon="📋"
          label="Attendance"
          value={`${attendance.percentage || 0}%`}
          to="/my-attendance"
          color={(attendance.percentage || 0) >= 75 ? '#388E3C' : '#D32F2F'}
        />
        <StatCard icon="📝" label="Assessments" value={upcomingAssessments.length} to="/my-exams" />
        <StatCard icon="🏫" label="Grade/Class" value={data.grade || 'Not assigned'} to="/my-programme" />
        <StatCard icon="🏛️" label="Programme" value={data.programme || 'No Programme'} to="/my-programme" />
        <StatCard icon="✉️" label="Unread Messages" value={data.unreadMessages || 0} to="/messages" />
      </div>

      <SectionCard title="Upcoming Assessments">
        {upcomingAssessments.length === 0 ? (
          <EmptyState message="No upcoming assessments." />
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Assessment</th>
                  <th>Module</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {upcomingAssessments.map(item => (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td>{item.subject}</td>
                    <td>{new Date(item.date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </>
  );
};

const ParentDashboard = ({ data }) => {
  const children = data.children || [];

  return (
    <>
      <div className="stats-grid">
        <StatCard icon="👨‍👩‍👧" label="Linked Children" value={data.stats?.children || 0} to="/my-children" />
        <StatCard icon="✉️" label="Unread Messages" value={data.unreadMessages || 0} to="/messages" />
      </div>

      <SectionCard title="Children">
        {children.length === 0 ? (
          <EmptyState message="No children linked to this account yet." />
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                {children.map(child => (
                  <tr key={child.id}>
                    <td>{child.name}</td>
                    <td>{child.grade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { status: setupStatus } = useSetupStatus();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.get('dashboard');
        setDashboardData(response);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load dashboard.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (!user) return null;
  if (loading) return <LoadingSpinner message="Loading dashboard..." />;

  const renderDashboard = () => {
    const data = dashboardData || {};
    switch (user.role) {
      case 'admin':
        return <AdminDashboard data={data} setupStatus={setupStatus} />;
      case 'teacher':
        return <TeacherDashboard data={data} />;
      case 'student':
        return <StudentDashboard data={data} />;
      case 'parent':
        return <ParentDashboard data={data} />;
      default:
        return <EmptyState message="No dashboard is available for this role." />;
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <Badge text={ROLE_LABELS[user.role] || user.role} color={ROLE_COLORS[user.role] || 'var(--primary-color)'} />
        </div>
      </div>

      <div className="dashboard-welcome" style={{ marginBottom: '1.5rem' }}>
        <h2>Welcome back, {user.firstName}!</h2>
        <p style={{ color: 'var(--text-light)', margin: 0 }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {error ? <div className="error-message">{error}</div> : renderDashboard()}
    </div>
  );
};

export default Dashboard;
